import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Fortune, Point } from "./Fortune";

import { Text, Circle, Group, Layer, Line, Rect, Stage } from "react-konva";
import Konva from "konva";

function Main() {
  const [points, setPoints] = useState([
    new Point(140, 180),
    new Point(230, 200),
    new Point(810, 60),
    new Point(500, 600),
  ]);
  const [sortedPoints, setSortedPoints] = useState([
    new Point(140, 180),
    new Point(230, 200),
    new Point(810, 60),
    new Point(500, 600),
  ]);
  const [mousePoint, setMousePoint] = useState(new Point(-1, -1));
  const [fortune, setFortune] = useState(new Fortune(points, 300, 300));
  const divRef = useRef(null);

  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const groupRef: React.RefObject<Konva.Group> = useRef();

  useEffect(() => {
    let clonepoints = [...points];
    clonepoints.sort((a, b) => a.distance(mousePoint) - b.distance(mousePoint));
    setSortedPoints(clonepoints);
  }, [points, mousePoint]);

  useEffect(() => {
    let inputPoints = points;
    if (inputPoints.length == 0) inputPoints = [new Point(0, 0)];
    let newFortune = new Fortune(
      inputPoints,
      dimensions.width,
      dimensions.height
    );
    newFortune.update();
    setFortune(newFortune);
  }, [dimensions, points]);

  useEffect(() => {
    if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetHeight,
      });
    }
    if (divRef.current?.offsetHeight == 0 && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetWidth,
      });
    }
  }, [divRef]);
  const [showDistance, setShowDistance] = useState(false);

  const cleanAllPoints = () => {
    setPoints([]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 7 }}>
        <Controls
          showDistance={showDistance}
          setShowDistance={setShowDistance}
          cleanAllPoints={cleanAllPoints}
        />
      </Box>
      <Box
        sx={{
          flex: 13,
          minHeight: 600,
          backgroundColor: "#efefef",
          position: "relative",
          height: "100%",
        }}
        ref={divRef}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            margin: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          ></Box>
        </Box>
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          onMouseMove={() => {
            let pos = groupRef.current.getRelativePointerPosition();
            setMousePoint(new Point(pos.x, pos.y));
          }}
          onMouseLeave={() => {
            setMousePoint(new Point(-10, -10));
          }}
          onClick={(e) => {
            let pos = groupRef.current.getRelativePointerPosition();
            setPoints([...points, new Point(pos.x, pos.y)]);
          }}
        >
          <Layer>
            <Group ref={groupRef}>
              <Circle
                radius={5}
                fill="#33ccff"
                x={mousePoint.x}
                y={mousePoint.y}
              />
              {points.map((p, i) => (
                <Circle key={i} radius={10} fill="orange" x={p.x} y={p.y} />
              ))}
              {showDistance &&
                mousePoint.x > 0 &&
                sortedPoints.slice(0, 3).map((p, i) => (
                  <>
                    <Line
                      key={i}
                      strokeWidth={5}
                      stroke={"#33ccff"}
                      points={[p.x, p.y, mousePoint.x, mousePoint.y]}
                    />
                    <Text
                      x={(p.x + mousePoint.x) / 2}
                      y={(p.y + mousePoint.y) / 2}
                      fontSize={16}
                      text={p.distance(mousePoint).toFixed(2)}
                    />
                  </>
                ))}
              {fortune.edges
                .filter((e) => e)
                .map((e, i) => {
                  return (
                    <Line
                      key={i}
                      strokeWidth={10}
                      stroke={"grey"}
                      points={[e.start.x, e.start.y, e.end.x, e.end.y]}
                    />
                  );
                })}
              {fortune.voronoi_vertex
                .filter((e) => e)
                .map((p, i) => {
                  return (
                    <Circle key={i} radius={5} fill="black" x={p.x} y={p.y} />
                  );
                })}
            </Group>
          </Layer>
        </Stage>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function Voronoi(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of Voronoi Diagram</title>
        <meta
          name="description"
          content="Explore interactive Voronoi diagrams with real-time visualization. Add points, see instant results, and measure distances to the three closest sites. Perfect for learning and visualizing spatial structures."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default Voronoi;
